import { useCallback, useEffect, useState } from "react";
import { CustomerGroup } from "../../types/customerGroup";
import customerGroupService from "../../services/customerGroupService";
import PageBreadcrumb from "../../shared/PageBreadCrumb";
import CustomerGroupTable from "./list/CustomerGroupTable";
import RightDrawer from "../../shared/components/form/RightDrawer";
import CustomerGroupForm from "./form/CustomerGroupForm";
import CustomerGroupDetailsDrawer from "./components/CustomerGroupDetailsDrawer";
import useDebounce from "../../hooks/useDebounce";
import ErrorAlert from "../../shared/components/ui/ErrorAlert";

const ITEMS_PER_PAGE = 6;
const DEBOUNCE_DELAY = 500;

export default function CustomerGroupList() {
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<CustomerGroup | null>(null);
  const [viewingItem, setViewingItem] = useState<CustomerGroup | null>(null);
  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const fetchCustomerGroups = useCallback(async (abortController?: AbortController) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: `${sortField}:${sortOrder}`,
        search: debouncedSearchTerm,
        status: status as 'active' | 'inactive' | undefined,
      };

      const res = await customerGroupService.getCustomerGroups(params);

      setCustomerGroups(res?.data || []);
      setTotalItems(res?.meta?.totalDocs || 0);
    } catch (err: any) {
      if (err.name === 'CanceledError') {
        console.log('Request was canceled');
        return;
      }

      console.error("API Error:", err);
      setCustomerGroups([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, status, page, sortField, sortOrder]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchCustomerGroups(abortController);

    return () => abortController.abort();
  }, [fetchCustomerGroups]);

  const handleAdd = (item: CustomerGroup) => {
    setEditingItem(null);
    setOpenForm(false);
    
    setPage(1);
    const refetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = {
          page: 1,
          limit: ITEMS_PER_PAGE,
          sortBy: `${sortField}:${sortOrder}`,
          search: debouncedSearchTerm,
          status: status as 'active' | 'inactive' | undefined,
        };

        const res = await customerGroupService.getCustomerGroups(params);
        
        setCustomerGroups(res?.data || []);
        setTotalItems(res?.meta?.totalDocs || 0);
      } catch (err: any) {
        console.error("API Error:", err);
        setCustomerGroups([]);
        setTotalItems(0);
      } finally {
        setIsLoading(false);
      }
    };
    
    refetchData();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    fetchCustomerGroups();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 relative">
      <PageBreadcrumb pageTitle="Customer Group" />
      
      {error && (
        <ErrorAlert 
          message={error} 
          onClose={() => setError(null)} 
          className="mb-4"
        />
      )}
      
      <CustomerGroupTable
        data={customerGroups}
        search={searchTerm}
        setSearch={setSearchTerm}
        status={status}
        setStatus={setStatus}
        page={page}
        setPage={handlePageChange}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        onView={(item) => {
          setViewingItem(item);
          setShowDetailView(true);
        }}
        onEdit={(item) => {
          setEditingItem(item);
          setOpenForm(true);
        }}
        onAdd={() => {
          setEditingItem(null);
          setOpenForm(true);
        }}
        onRefresh={handleRefresh}
        isLoading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
      />
      
      <RightDrawer 
        open={openForm} 
        title={editingItem ? "Edit Customer Group" : "Add Customer Group"}
        onClose={() => {
          setOpenForm(false);
          setEditingItem(null);
        }}
      >
        <CustomerGroupForm 
          editingItem={editingItem} 
          onAdd={handleAdd} 
          onClose={() => {
            setOpenForm(false);
            setEditingItem(null);
          }} 
        />
      </RightDrawer>

      <CustomerGroupDetailsDrawer
        customerGroup={viewingItem}
        isOpen={showDetailView}
        onClose={() => {
          setShowDetailView(false);
          setViewingItem(null);
        }}
      />
    </div>
  );
}
